import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert, Form, Row, Col, Nav } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * URL Processing Log Component
 * Admin view of all URL processing attempts for audit trail
 */
const UrlProcessingLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('production'); // 'production' or 'test'

  useEffect(() => {
    fetchLogs();
  }, [page, statusFilter, viewMode]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const dryRun = viewMode === 'test';
      const params = new URLSearchParams({ page, limit: 50, dryRun });
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await fetch(`/api/admin/tools/url-log?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      setLogs(data.logs || []);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to fetch URL log');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const statusBadge = (status) => {
    const colors = {
      success: 'success',
      failed: 'danger',
      blocked: 'warning',
      paywall: 'info',
      cached: 'secondary'
    };
    return <Badge bg={colors[status] || 'secondary'}>{status}</Badge>;
  };

  const truncateUrl = (url, len = 50) => {
    if (!url) return '-';
    return url.length > len ? url.substring(0, len) + '...' : url;
  };

  if (loading && logs.length === 0) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      {/* View Mode Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link
            active={viewMode === 'production'}
            onClick={() => { setViewMode('production'); setPage(1); }}
            style={{ cursor: 'pointer' }}
          >
            Production
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={viewMode === 'test'}
            onClick={() => { setViewMode('test'); setPage(1); }}
            style={{ cursor: 'pointer' }}
          >
            Test/Mock
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {viewMode === 'test' && (
        <Alert variant="info" className="mb-4">
          Showing URL logs from dry run/test mode.
        </Alert>
      )}

    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">URL Processing Log</h5>
        <Form.Select
          style={{ width: 'auto' }}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="blocked">Blocked</option>
          <option value="paywall">Paywall</option>
          <option value="cached">Cached</option>
        </Form.Select>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Table responsive hover size="sm">
          <thead>
            <tr>
              <th>Time</th>
              <th>URL</th>
              <th>Domain</th>
              <th>Status</th>
              <th>IP Hash</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  {formatDate(log.created_at)}
                </td>
                <td style={{ fontSize: '0.875rem' }}>
                  <span title={log.url}>{truncateUrl(log.url)}</span>
                </td>
                <td><code style={{ fontSize: '0.75rem' }}>{log.domain || '-'}</code></td>
                <td>{statusBadge(log.status)}</td>
                <td style={{ fontSize: '0.75rem' }}>
                  <code>{log.ip_address_hash?.substring(0, 8) || '-'}</code>
                </td>
                <td style={{ fontSize: '0.875rem' }}>
                  {log.failure_reason || (log.article_id ? `Article #${log.article_id}` : '-')}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {logs.length === 0 && (
          <p className="text-muted text-center py-3">No URL processing logs found</p>
        )}

        {pagination && pagination.totalPages > 1 && (
          <Row className="mt-3">
            <Col>
              <small className="text-muted">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </small>
            </Col>
            <Col className="text-end">
              <button
                className="btn btn-sm btn-outline-secondary me-2"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
    </AdminToolsLayout>
  );
};

export default UrlProcessingLog;
